import { Clock, X } from "lucide-react";
import * as React from "react";

import { TimePeriodSelect } from "./PeriodSelect";
import { TimePickerInput } from "./TimePickerInput";
import { Period, TimePickerTypeEnum } from "./TimePickerUtils";

import Icons from "@/components/Icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/core/generalUtils";
import {
  dateToSec,
  localTimezoneToUtc,
  utcToLocalTimezone,
} from "@/utils/datetime/dateTimeHelper";

interface TimePickerDemoProps {
  date?: Date | string;
  setDate: (date: Date | undefined) => void;
  limit?: Date | undefined;
  disabled?: boolean;
  AMPM?: Period;
  setAMPM?: React.Dispatch<React.SetStateAction<Period>>;
  showIcon?: boolean;
  className?: string;
  isUtcTime?: boolean;
  showResetButton?: boolean;
  onReset?: () => void;
}

export const TimePicker12: React.FC<TimePickerDemoProps> = ({
  date,
  setDate,
  limit,
  disabled,
  AMPM,
  setAMPM,
  showIcon = false,
  className,
  isUtcTime = false,
  showResetButton = false,
  onReset,
}) => {
  const [period, setPeriod] = React.useState<Period>(AMPM || "AM");

  // Sync internal period state with AMPM prop
  React.useEffect(() => {
    if (AMPM) {
      setPeriod(AMPM);
    }
  }, [AMPM]);
  const minuteRef = React.useRef<HTMLInputElement>(null);
  const hourRef = React.useRef<HTMLInputElement>(null);
  const periodRef = React.useRef<HTMLButtonElement>(null);

  // Convert UTC date to local timezone for display
  const displayDate = React.useMemo(() => {
    if (!date) {
      return undefined;
    }

    const normalizedDate = typeof date === "string" ? new Date(date) : date;

    return isUtcTime ? utcToLocalTimezone(normalizedDate) : normalizedDate;
  }, [date, isUtcTime]);

  // Handle date changes with timezone conversion
  const handleDateChange = React.useCallback(
    (newDate: Date | undefined) => {
      if (!newDate) {
        setDate(newDate);

        return;
      }

      // If isUtcTime is true, convert local timezone back to UTC before calling setDate
      const dateToSet = isUtcTime ? localTimezoneToUtc(newDate) : newDate;
      setDate(dateToSet);
    },
    [setDate, isUtcTime],
  );

  const handleReset = (): void => {
    setDate(undefined);
    onReset?.();
  };

  React.useEffect(() => {
    if (setAMPM && displayDate) {
      setAMPM(dateToSec(displayDate) > 43199 ? "PM" : "AM");
    }
  }, [displayDate, setAMPM]);

  return (
    <div
      className={cn(
        "flex items-center border rounded-md bg-white dark:bg-inherit",
        !date && "opacity-50",
        className,
      )}
    >
      <div className='grid text-center'>
        <TimePickerInput
          picker={TimePickerTypeEnum.TWELVE_HOURS}
          period={period}
          date={displayDate}
          setDate={handleDateChange}
          ref={hourRef}
          onRightFocus={() => minuteRef.current?.focus()}
          className='border-0'
          disabled={disabled}
          limit={limit}

          // defaultValue={newDateObject?.getHours()}
        />
      </div>
      <Icons name='colon-icon' size={15} />
      <div className='grid text-center'>
        <TimePickerInput
          picker={TimePickerTypeEnum.MINUTES}
          id='minutes12'
          date={displayDate}
          setDate={handleDateChange}
          ref={minuteRef}
          onLeftFocus={() => hourRef.current?.focus()}
          className='border-0'
          disabled={disabled}
        />
      </div>
      <div className='grid text-center'>
        <TimePeriodSelect
          // period={date!?.getHours() >= 12 ? 'PM' : 'AM'}
          disabled={disabled || !date}
          period={period}
          setPeriod={setPeriod}
          date={displayDate}
          setDate={handleDateChange}
          ref={periodRef}
        />
      </div>
      {showIcon && <Clock className='w-4 h-4 mr-2' />}

      {showResetButton && date && (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-6 w-6 p-0 ml-1'
          onClick={handleReset}
          disabled={disabled}
        >
          <X className='h-3 w-3' />
        </Button>
      )}
    </div>
  );
};
